import React, { useContext } from 'react';
import { UserRoles, userRolesToLabelMap } from '@bb/users';
import { RouteComponentProps } from 'react-router';
import { LayoutContext, AuthContext } from 'contexts';
import { update } from 'api/TeamController';
import errorReporter from '@app/errorReporter';
import {
  Avatar,
  Button,
  Dropdown,
  Icon,
  Space,
  TableProps,
  Menu,
  MenuItem,
  message,
  Text,
} from 'components';
import { useHistory } from 'react-router-dom';
import { useTableResizer } from 'hooks';
import { StyledTable, UserStatusTag } from '../../../components';
import ManageUsersContext from '../ManageUsersContext';
import ManageUsersPagination from './ManageUsersPagination';
import { addDatacy, addDatacyToHeader, TeamMember } from '../util';
import { TeamGroupModal } from '../TeamGroupModal';
import TeamMemberRemovalConfirmation from './TeamMemberRemovalConfirmation';

export interface MatchParams {
  teamId?: string;
}

export interface TeamMemberEntry extends Omit<TeamMember, 'role'> {
  key: string;
  role: string;
  enumRole: UserRoles;
}

const TeamsMembersTable: React.FC<RouteComponentProps<MatchParams>> = ({
  match,
}) => {
  const teamId = match.params.teamId || '';
  const { transition, setAppTitle } = React.useContext(LayoutContext);
  const { auth } = React.useContext(AuthContext);
  const {
    teamId: teamIdFromContext,
    isTeamMemberLoading: isLoading,
    selectedTeamInfo,
    setBreadcrumbRoutes,
    fetchTeamMembers,
    setTeamId,
    setSearchText,
  } = React.useContext(ManageUsersContext);
  const prevTeamId = React.useRef<string>('');
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [popconfirmVisibility, setPopconfirmVisibility] = React.useState(false);

  const history = useHistory();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const tableWrapperRef = React.useRef<HTMLDivElement>();

  const { tableHeight, tableHeaderHeight } = useTableResizer({
    wrapperRef: tableWrapperRef,
  });

  React.useEffect(() => {
    prevTeamId.current = teamIdFromContext;
    setTeamId(teamId);
    setSearchText('');
    return () => {
      setSearchText('');
    };
  }, [teamId, setTeamId, setSearchText]);

  React.useEffect(() => {
    if (!selectedTeamInfo && prevTeamId.current) setTeamId(prevTeamId.current);
  }, [selectedTeamInfo]);

  React.useEffect(() => {
    if (selectedTeamInfo?.name) {
      setAppTitle(selectedTeamInfo.name);
      setBreadcrumbRoutes([
        {
          breadcrumbName: 'Teams',
          path: '/users/list?tabKey=teams',
        },
        {
          breadcrumbName: selectedTeamInfo.name,
          path: `/users/list/team/${selectedTeamInfo.id}`,
        },
      ]);
    }
  }, [
    selectedTeamInfo?.id,
    selectedTeamInfo?.name,
    setAppTitle,
    setBreadcrumbRoutes,
  ]);

  React.useEffect(() => () => setTeamId(''), [setTeamId]);

  const handleRemoveMember = async (userId: string) => {
    try {
      setTeamId('');
      setIsDeleting(true);
      await update(teamId, {
        ...selectedTeamInfo,
        usersToRemove: [userId],
      });
    } catch (err) {
      message.error('Error removing user from team');
      errorReporter.error(err);
    } finally {
      setTeamId(teamId);
      setIsDeleting(false);
    }
  };

  const columns: TableProps<TeamMemberEntry>['columns'] = [
    {
      width: 250,
      title: 'Name',
      key: 'name',
      dataIndex: '',
      render: (_, user) => (
        <Space align="center" size="middle">
          <Avatar user={user} />
          <Text>{`${user.firstName} ${user.lastName || ''}`.trim()}</Text>
        </Space>
      ),
      onCell: addDatacy('users-name'),
      onHeaderCell: addDatacyToHeader('users-name'),
    },
    {
      width: 80,
      title: 'Type',
      key: 'type',
      dataIndex: 'role',
      align: 'center',
      onCell: addDatacy('users-type'),
      onHeaderCell: addDatacyToHeader('users-type'),
    },
    {
      width: 100,
      title: 'Status',
      key: 'status',
      dataIndex: '',
      align: 'center',
      render: (_, { disabled, isUnverified }) => (
        <UserStatusTag disabled={disabled} isUnverified={isUnverified} />
      ),
      onCell: addDatacy('users-status'),
      onHeaderCell: addDatacyToHeader('users-status'),
    },
    {
      width: 200,
      title: 'Email',
      key: 'email',
      dataIndex: 'email',
      onCell: addDatacy('users-email'),
      onHeaderCell: addDatacyToHeader('users-email'),
    },
    {
      width: 125,
      title: 'Branch',
      key: 'location',
      dataIndex: 'location',
      onCell: addDatacy('users-branch'),
      onHeaderCell: addDatacyToHeader('users-branch'),
    },
    {
      width: 300,
      title: 'Address',
      key: 'address',
      dataIndex: 'address',
      onCell: addDatacy('users-address'),
      onHeaderCell: addDatacyToHeader('users-address'),
    },
    {
      width: 80,
      title: '',
      key: 'actions',
      dataIndex: '',
      align: 'center',
      render: (_, user) => (
        <Dropdown
          placement="topLeft"
          trigger={['click']}
          getPopupContainer={node => node.parentElement}
          dropdownRender={() => (
            <div onClick={e => e.stopPropagation()} data-cy="users-dropdown">
              <TeamMemberRemovalConfirmation
                userId={user.id}
                userRole={user.enumRole}
                visible={popconfirmVisibility}
                handleCancel={() => setPopconfirmVisibility(false)}
                handleRemove={() => handleRemoveMember(user.id)}
              >
                <div>
                  <Menu>
                    <MenuItem
                      key={'remove'}
                      onClickCapture={e => {
                        setPopconfirmVisibility(true);
                        e.stopPropagation();
                      }}
                      data-cy="users-dropdown-remove"
                    >
                      Remove from Team
                    </MenuItem>
                  </Menu>
                </div>
              </TeamMemberRemovalConfirmation>
            </div>
          )}
        >
          <Button type="text" size="small" onClick={e => e.stopPropagation()}>
            <Icon type="ellipsis" width={25} />
          </Button>
        </Dropdown>
      ),
      onCell: addDatacy('users-actions'),
    },
  ];
  const processedColumns = columns.filter(Boolean);

  const dataSource: TeamMemberEntry[] =
    auth &&
    selectedTeamInfo?.users?.map(
      ({
        id,
        firstName,
        lastName,
        email,
        location,
        address,
        role,
        avatarFilestackId,
        disabled,
        isUnverified,
      }) => ({
        id,
        key: id,
        firstName,
        lastName,
        email,
        disabled,
        isUnverified,
        enumRole: role,
        role: userRolesToLabelMap[role],
        avatarFilestackId,
        location: auth?.org?.locations?.[Number(location)]?.name,
        address:
          auth?.org?.addresses?.[Number(location)]?.[Number(address)].address,
      }),
    );

  return (
    <>
      <div ref={tableWrapperRef}>
        <StyledTable
          style={{ transition: `all ${transition}s` }}
          tableLayout="fixed"
          pagination={false}
          $height={tableHeight}
          $headerHeight={tableHeaderHeight}
          showSorterTooltip={false}
          loading={isLoading || isDeleting}
          dataSource={dataSource}
          columns={processedColumns}
          scroll={{ x: '100%', y: `calc(100% - ${tableHeaderHeight}px)` }}
          onRow={user => ({
            onClick: () => history.push(`/users/edit/${user.id}`),
          })}
        />
        <ManageUsersPagination hidden />
      </div>
      <TeamGroupModal destroyOnClose onSave={() => fetchTeamMembers(true)} />
    </>
  );
};

export default TeamsMembersTable;
